// src/entities/sets/sets.api.js
import { api } from '../../api/rtk';
import { doc, getDoc, setDoc, deleteDoc, collection, getDocs, updateDoc } from 'firebase/firestore';
import { db } from '../../utils/firebase/firebase.utils';
import { setDocPath, setItemDocPath } from '../../utils/firebase/paths';

export const setsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getSets: build.query({
      async queryFn(uid) {
        const snap = await getDocs(collection(db, `users/${uid}/sets`));
        return { data: snap.docs.map(d => ({ id: d.id, ...d.data() })) };
      },
      providesTags: ['Set']
    }),
    getSet: build.query({
      async queryFn({ uid, setId }) {
        const snap = await getDoc(doc(db, setDocPath(uid, setId)));
        return { data: snap.exists() ? { id: setId, ...snap.data() } : null };
      },
      providesTags: (_r,_e,{ setId }) => [{ type: 'Set', id: setId }, { type: 'SetItems', id: setId }]
    }),
    getSetItems: build.query({
      async queryFn({ uid, setId }) {
        const snap = await getDocs(collection(db, `users/${uid}/sets/${setId}/items`));
        return { data: snap.docs.map(d => ({ id: d.id, ...d.data() })) };
      },
      providesTags: (_r,_e,{ setId }) => [{ type: 'SetItems', id: setId }]
    }),
    addSetItem: build.mutation({
      async queryFn({ uid, setId, itemId, data }) {
        
        // Add the item to the set
        await setDoc(doc(db, setItemDocPath(uid, setId, itemId)), data, { merge: true });
        
        // Update the set's itemCount
        const setDocRef = doc(db, setDocPath(uid, setId));
        const setDocSnapshot = await getDoc(setDocRef);
        const currentItemCount = setDocSnapshot.exists() ? (setDocSnapshot.data().itemCount || 0) : 0;
        const quantityToAdd = data.quantity || 1;
        
        
        await updateDoc(setDocRef, {
          itemCount: currentItemCount + quantityToAdd,
          updatedAt: new Date().toISOString()
        });
        
        
        return { data: { setId } };
      },
      invalidatesTags: (_r,_e,{ setId }) => [{ type: 'SetItems', id: setId }, { type: 'Set', id: setId }]
    }),
    removeSetItem: build.mutation({
      async queryFn({ uid, setId, itemId }) {
        // Get the item data to know how much to decrement
        const itemDoc = await getDoc(doc(db, setItemDocPath(uid, setId, itemId)));
        const itemQuantity = itemDoc.exists() ? (itemDoc.data().quantity || 1) : 1;
        
        // Delete the item
        await deleteDoc(doc(db, setItemDocPath(uid, setId, itemId)));
        
        // Update the set's itemCount
        const setDocRef = doc(db, setDocPath(uid, setId));
        const setDocSnapshot = await getDoc(setDocRef);
        const currentItemCount = setDocSnapshot.exists() ? (setDocSnapshot.data().itemCount || 0) : 0;
        
        await updateDoc(setDocRef, {
          itemCount: Math.max(0, currentItemCount - itemQuantity),
          updatedAt: new Date().toISOString()
        });
        
        return { data: { setId } };
      },
      invalidatesTags: (_r,_e,{ setId }) => [{ type: 'SetItems', id: setId }, { type: 'Set', id: setId }]
    }),
    createSet: build.mutation({
      async queryFn({ uid, setData }) {
        const { title, description, tags } = setData;
        
        // Validate required fields
        if (!title || !title.trim()) {
          throw new Error('Set title is required');
        }

        // Generate a unique set ID using timestamp and random string
        const setId = `set_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        
        const setDocument = {
          title: title.trim(),
          description: description?.trim() || '',
          tags: Array.isArray(tags) ? tags : [],
          // Set default values for compatibility
          sport: 'baseball',
          year: new Date().getFullYear(),
          manufacturer: 'topps',
          series: 'series-1',
          cardType: 'base',
          cardTypeCategory: 'regular',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          itemCount: 0,
          isActive: true
        };

        await setDoc(doc(db, setDocPath(uid, setId)), setDocument);
        
        return { data: { setId, ...setDocument } };
      },
      invalidatesTags: ['Set']
    }),
    updateSet: build.mutation({
      async queryFn({ uid, setId, setData }) {
        const { title, description, tags } = setData;
        
        // Validate required fields
        if (!title || !title.trim()) {
          throw new Error('Set title is required');
        }

        const updateData = {
          title: title.trim(),
          description: description?.trim() || '',
          tags: Array.isArray(tags) ? tags : [],
          updatedAt: new Date().toISOString()
        };

        await updateDoc(doc(db, setDocPath(uid, setId)), updateData);
        
        return { data: { setId, ...updateData } };
      },
      invalidatesTags: (_r,_e,{ setId }) => [{ type: 'Set', id: setId }]
    }),
    deleteSet: build.mutation({
      async queryFn({ uid, setId }) {
        // Delete all set items first
        const itemsSnapshot = await getDocs(collection(db, `users/${uid}/sets/${setId}/items`));
        const deleteItemPromises = itemsSnapshot.docs.map(itemDoc => 
          deleteDoc(doc(db, setItemDocPath(uid, setId, itemDoc.id)))
        );
        await Promise.all(deleteItemPromises);
        
        // Delete the set document
        await deleteDoc(doc(db, setDocPath(uid, setId)));
        
        return { data: { setId } };
      },
      invalidatesTags: ['Set']
    }),
    deleteSets: build.mutation({
      async queryFn({ uid, setIds }) {
        if (!Array.isArray(setIds) || setIds.length === 0) {
          throw new Error('Set IDs array is required and cannot be empty');
        }

        const deletePromises = setIds.map(async (setId) => {
          // Delete all set items first
          const itemsSnapshot = await getDocs(collection(db, `users/${uid}/sets/${setId}/items`));
          const deleteItemPromises = itemsSnapshot.docs.map(itemDoc => 
            deleteDoc(doc(db, setItemDocPath(uid, setId, itemDoc.id)))
          );
          await Promise.all(deleteItemPromises);
          
          // Delete the set document
          await deleteDoc(doc(db, setDocPath(uid, setId)));
          
          return { setId };
        });

        const results = await Promise.all(deletePromises);
        
        return { data: { deletedSets: results } };
      },
      invalidatesTags: ['Set']
    }),
    mergeSets: build.mutation({
      async queryFn({ uid, sourceSetIds, mergedSetData }) {
        const { title, description, tags } = mergedSetData;
        
        // Validate required fields
        if (!title || !title.trim()) {
          throw new Error('Merged set title is required');
        }
        
        if (!Array.isArray(sourceSetIds) || sourceSetIds.length < 2) {
          throw new Error('At least 2 source sets are required for merging');
        }

        // Generate a unique set ID for the merged set
        const mergedSetId = `set_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        
        // Get all source sets and their items
        const sourceSetsData = [];
        const allItems = new Map(); // Use Map to handle duplicate items by combining quantities
        
        for (const setId of sourceSetIds) {
          // Get set data
          const setDoc = await getDoc(doc(db, setDocPath(uid, setId)));
          if (!setDoc.exists()) {
            throw new Error(`Source set ${setId} not found`);
          }
          sourceSetsData.push({ id: setId, ...setDoc.data() });
          
          // Get set items
          const itemsSnapshot = await getDocs(collection(db, `users/${uid}/sets/${setId}/items`));
          for (const itemDoc of itemsSnapshot.docs) {
            const itemData = itemDoc.data();
            const itemId = itemDoc.id;
            
            if (allItems.has(itemId)) {
              // If item already exists, combine quantities
              const existingItem = allItems.get(itemId);
              allItems.set(itemId, {
                ...existingItem,
                quantity: (existingItem.quantity || 1) + (itemData.quantity || 1)
              });
            } else {
              // Add new item
              allItems.set(itemId, itemData);
            }
          }
        }

        // Create the merged set document
        const mergedSetDocument = {
          title: title.trim(),
          description: description?.trim() || '',
          tags: Array.isArray(tags) ? tags : [],
          // Set default values for compatibility
          sport: 'baseball',
          year: new Date().getFullYear(),
          manufacturer: 'topps',
          series: 'series-1',
          cardType: 'base',
          cardTypeCategory: 'regular',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          itemCount: Array.from(allItems.values()).reduce((total, item) => total + (item.quantity || 1), 0),
          isActive: true,
          // Add metadata about the merge
          mergedFrom: sourceSetIds,
          mergedAt: new Date().toISOString()
        };

        // Create the merged set
        await setDoc(doc(db, setDocPath(uid, mergedSetId)), mergedSetDocument);

        // Add all items to the merged set
        const addItemPromises = Array.from(allItems.entries()).map(([itemId, itemData]) =>
          setDoc(doc(db, setItemDocPath(uid, mergedSetId, itemId)), itemData, { merge: true })
        );
        await Promise.all(addItemPromises);

        // Delete all source sets and their items
        const deleteSourcePromises = sourceSetIds.map(async (setId) => {
          // Delete all set items first
          const itemsSnapshot = await getDocs(collection(db, `users/${uid}/sets/${setId}/items`));
          const deleteItemPromises = itemsSnapshot.docs.map(itemDoc => 
            deleteDoc(doc(db, setItemDocPath(uid, setId, itemDoc.id)))
          );
          await Promise.all(deleteItemPromises);
          
          // Delete the set document
          await deleteDoc(doc(db, setDocPath(uid, setId)));
        });
        await Promise.all(deleteSourcePromises);
        
        return { 
          data: { 
            mergedSetId, 
            ...mergedSetDocument,
            sourceSets: sourceSetsData,
            totalItems: allItems.size,
            totalQuantity: mergedSetDocument.itemCount
          } 
        };
      },
      invalidatesTags: ['Set']
    }),
  })
});

export const {
  useGetSetsQuery,
  useGetSetQuery,
  useGetSetItemsQuery,
  useAddSetItemMutation,
  useRemoveSetItemMutation,
  useCreateSetMutation,
  useUpdateSetMutation,
  useDeleteSetMutation
} = setsApi;